// =====================================================================
//  ADMIN CONSOLE, LEAVE
// ---------------------------------------------------------------------
//  An approval workstation, not a report.
//
//  The Pending tab opens first and is the working view: every request
//  waiting on a decision, with approve/reject on the row and a drawer for
//  the full context (who they are, what they've already taken this year,
//  who else is out on those dates). "Who else is out" matters, approving
//  three people onto the same week is the mistake this screen exists to
//  prevent.
// =====================================================================

import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  CalendarCheck,
  CalendarDays,
  CalendarX,
  Check,
  CheckCheck,
  Download,
  Hourglass,
  Users,
  X,
} from 'lucide-react';

import backendApi from '../../api/backendApi';
import DataTable from '../DataTable';
import {
  Button,
  ChartTooltip,
  Chip,
  ConfirmDialog,
  Drawer,
  EmptyState,
  FilterChip,
  Grid,
  KeyValue,
  KpiTile,
  Meter,
  Notice,
  PageHeader,
  Panel,
  PanelBody,
  PanelHead,
  PersonCell,
  SearchInput,
  StatusChip,
  Tabs,
  Toolbar,
  ToolbarSpacer,
  toast,
  useDebounced,
} from '../ui';
import { CHART_SEQUENCE } from '../theme';
import {
  DASH,
  daysBetween,
  distinct,
  downloadCsv,
  fmtDate,
  fmtNumber,
  matchesSearch,
  orDash,
  parseDate,
  timeAgo,
  toISODate,
} from '../format';

export default function LeavePage({ can }) {
  const queryClient = useQueryClient();
  // Was `['admin','hr'].includes(role)`, which hid the decision controls from
  // managers even though they hold leave.approve, the first step of the
  // standard "manager, then HR" chain.
  const canDecide = can('leave.approve');

  const [tab, setTab] = useState('pending');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [active, setActive] = useState(null);
  const [bulkConfirm, setBulkConfirm] = useState(null);
  const [selected, setSelected] = useState([]);

  const debouncedSearch = useDebounced(search, 250);

  /* ------------------------------- data ------------------------------- */
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'leave-all'],
    queryFn: () => backendApi.entities.LeaveRequest.page({ limit: 500, sort: 'created_at', order: 'desc' }),
  });

  // Pinned identity: a fresh `[]` each render would invalidate every memo below.
  const all = useMemo(() => data?.rows || [], [data]);

  const { data: byType = [] } = useQuery({
    queryKey: ['admin', 'leave-by-type'],
    queryFn: async () => {
      const overview = await backendApi.admin.overview();
      return overview?.leaveByType || [];
    },
  });

  /* ---------------------------- decisions ----------------------------- */
  const decide = useMutation({
    mutationFn: ({ id, decision }) => backendApi.admin.decideLeave(id, decision),
    onSuccess: (_row, { decision }) => {
      toast.success(`Request ${decision === 'approve' ? 'approved' : 'rejected'}`);
      setActive(null);
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (err) => toast.error(err.message || 'Could not record that decision'),
  });

  const bulkDecide = useMutation({
    mutationFn: async ({ ids, decision }) => {
      // Sequential rather than parallel: each decision fires a notification
      // trigger server-side, and a burst of them on a large selection is a
      // good way to exhaust the connection pool.
      const done = [];
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        done.push(await backendApi.admin.decideLeave(id, decision));
      }
      return done;
    },
    onSuccess: (done, { decision }) => {
      toast.success(`${done.length} request${done.length === 1 ? '' : 's'} ${decision === 'approve' ? 'approved' : 'rejected'}`);
      setSelected([]);
      setBulkConfirm(null);
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (err) => toast.error(err.message || 'Bulk decision failed'),
  });

  /* --------------------------- derived views -------------------------- */
  const pending = useMemo(
    () => all.filter((r) => String(r.status || '').toLowerCase() === 'pending'),
    [all]
  );

  const today = toISODate();

  // Everyone whose approved leave covers today, the "who's out" panel.
  const outToday = useMemo(
    () =>
      all.filter((r) => {
        if (String(r.status || '').toLowerCase() !== 'approved') return false;
        const start = r.start_date;
        const end = r.end_date || r.start_date;
        return start && start <= today && end >= today;
      }),
    [all, today]
  );

  const source = tab === 'pending' ? pending : all;

  const filtered = useMemo(
    () =>
      source.filter(
        (row) =>
          matchesSearch(row, debouncedSearch, ['employee_name', 'leave_type', 'reason', 'approved_by']) &&
          (!typeFilter || row.leave_type === typeFilter) &&
          (tab === 'pending' || !statusFilter || String(row.status).toLowerCase() === statusFilter.toLowerCase())
      ),
    [source, debouncedSearch, typeFilter, statusFilter, tab]
  );

  const leaveTypes = useMemo(() => distinct(all, 'leave_type'), [all]);

  const stats = useMemo(() => {
    const approved = all.filter((r) => String(r.status).toLowerCase() === 'approved');
    const rejected = all.filter((r) => String(r.status).toLowerCase() === 'rejected');
    const daysApproved = approved.reduce(
      (sum, r) => sum + (Number(r.days) || daysBetween(r.start_date, r.end_date)),
      0
    );
    return { approved: approved.length, rejected: rejected.length, daysApproved };
  }, [all]);

  /* ------------------------------ columns ----------------------------- */
  const columns = useMemo(
    () => [
      {
        key: 'employee_name',
        label: 'Employee',
        width: 210,
        render: (row) => <PersonCell name={row.employee_name} sub={row.leave_type} />,
      },
      {
        key: 'leave_type',
        label: 'Type',
        width: 130,
        render: (row) => (row.leave_type ? <Chip tone="info">{row.leave_type}</Chip> : DASH),
      },
      { key: 'start_date', label: 'From', width: 115, render: (row) => fmtDate(row.start_date) },
      { key: 'end_date', label: 'To', width: 115, render: (row) => fmtDate(row.end_date) },
      {
        key: 'days',
        label: 'Days',
        width: 70,
        align: 'right',
        render: (row) => row.days || daysBetween(row.start_date, row.end_date),
        csv: (row) => row.days || daysBetween(row.start_date, row.end_date),
      },
      {
        key: 'reason',
        label: 'Reason',
        render: (row) =>
          row.reason ? (
            <span title={row.reason} style={{ color: 'var(--ac-muted)' }}>
              {row.reason.length > 46 ? `${row.reason.slice(0, 45)}…` : row.reason}
            </span>
          ) : (
            DASH
          ),
      },
      {
        key: 'status',
        label: 'Status',
        width: 110,
        render: (row) => <StatusChip value={row.status} />,
      },
      {
        key: 'created_at',
        label: 'Requested',
        width: 110,
        render: (row) => <span title={fmtDate(row.created_at)}>{timeAgo(row.created_at)}</span>,
        csv: (row) => row.created_at,
      },
      {
        key: 'approved_by',
        label: 'Decided by',
        width: 130,
        defaultHidden: true,
        render: (row) => orDash(row.approved_by),
      },
    ],
    []
  );

  const exportCsv = () => {
    downloadCsv(`leave-${toISODate()}`, filtered, [
      { key: 'employee_name', label: 'Employee' },
      { key: 'leave_type', label: 'Type' },
      { key: 'start_date', label: 'From' },
      { key: 'end_date', label: 'To' },
      { key: 'days', label: 'Days', csv: (r) => r.days || daysBetween(r.start_date, r.end_date) },
      { key: 'status', label: 'Status' },
      { key: 'reason', label: 'Reason' },
      { key: 'approved_by', label: 'Decided by' },
    ]);
    toast.success(`Exported ${filtered.length} requests`);
  };

  return (
    <div className="ac-page ac-page--wide">
      <PageHeader
        title="Leave"
        sub="Approve requests, and see who is away before you decide."
        actions={
          <Button icon={Download} onClick={exportCsv} disabled={filtered.length === 0}>
            Export
          </Button>
        }
      />

      <Grid cols={4} style={{ marginBottom: 14 }}>
        <KpiTile
          label="Awaiting decision"
          value={fmtNumber(pending.length)}
          icon={Hourglass}
          tone={pending.length > 0 ? 'warn' : 'pos'}
          foot={pending.length === 0 ? 'Queue is clear' : 'Needs your action'}
        />
        <KpiTile label="Out today" value={fmtNumber(outToday.length)} icon={Users} foot="On approved leave" />
        <KpiTile
          label="Approved"
          value={fmtNumber(stats.approved)}
          icon={CalendarCheck}
          tone="pos"
          foot={`${fmtNumber(stats.daysApproved)} days total`}
        />
        <KpiTile label="Rejected" value={fmtNumber(stats.rejected)} icon={CalendarX} foot="All time" />
      </Grid>

      {pending.length > 0 && canDecide ? (
        <div style={{ marginBottom: 14 }}>
          <Notice tone="warning" title={`${pending.length} request${pending.length === 1 ? '' : 's'} waiting`}>
            Select rows to decide on several at once, or open a request to see the full context first.
          </Notice>
        </div>
      ) : null}

      <Grid cols={2} style={{ marginBottom: 14 }}>
        <Panel>
          <PanelHead title="Out today" sub={fmtDate(today)} />
          <PanelBody flush>
            {outToday.length === 0 ? (
              <EmptyState icon={Users} title="Everyone is in" text="Nobody is on approved leave today." />
            ) : (
              <div className="ac-feed">
                {outToday.map((row) => (
                  <div className="ac-feed-item" key={row.id}>
                    <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                      <div className="ac-feed-title">{row.employee_name}</div>
                      <div className="ac-feed-meta">
                        {row.leave_type} · until {fmtDate(row.end_date || row.start_date)}
                      </div>
                    </div>
                    <Chip tone="info">{row.days || daysBetween(row.start_date, row.end_date)}d</Chip>
                  </div>
                ))}
              </div>
            )}
          </PanelBody>
        </Panel>

        <Panel>
          <PanelHead title="Leave by type" sub="Approved days taken" />
          <PanelBody>
            {byType.length === 0 ? (
              <EmptyState icon={CalendarDays} title="No leave data" text="Approved leave will be summarised here." />
            ) : (
              <ResponsiveContainer width="100%" height={188}>
                <BarChart data={byType} margin={{ top: 6, right: 6, left: -22, bottom: 0 }} barCategoryGap="30%">
                  <CartesianGrid strokeDasharray="2 4" vertical={false} />
                  <XAxis dataKey="type" tickLine={false} axisLine={false} />
                  <YAxis tickLine={false} axisLine={false} width={44} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(15,20,25,0.04)' }} />
                  <Bar dataKey="days" name="Days" fill={CHART_SEQUENCE[0]} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </PanelBody>
        </Panel>
      </Grid>

      <Tabs
        items={[
          { key: 'pending', label: 'Pending', count: pending.length },
          { key: 'all', label: 'All requests', count: all.length },
        ]}
        value={tab}
        onChange={(next) => {
          setTab(next);
          setSelected([]);
        }}
      />

      <div style={{ marginTop: 14 }}>
        <DataTable
          columns={columns}
          rows={filtered}
          loading={isLoading}
          onRowClick={setActive}
          selectable={canDecide && tab === 'pending'}
          selected={selected}
          onSelectedChange={setSelected}
          defaultSort={{ key: 'created_at', dir: 'desc' }}
          emptyIcon={tab === 'pending' ? CheckCheck : CalendarDays}
          emptyTitle={tab === 'pending' ? 'Nothing pending' : 'No leave requests'}
          emptyText={
            tab === 'pending'
              ? 'Every request has been decided.'
              : 'Requests raised from the employee portal will appear here.'
          }
          bulkActions={(ids) => (
            <>
              <Button
                size="sm"
                icon={Check}
                onClick={() => setBulkConfirm({ ids, decision: 'approve' })}
                disabled={bulkDecide.isPending}
              >
                Approve
              </Button>
              <Button
                size="sm"
                icon={X}
                onClick={() => setBulkConfirm({ ids, decision: 'reject' })}
                disabled={bulkDecide.isPending}
              >
                Reject
              </Button>
            </>
          )}
          rowActions={
            canDecide
              ? (row) =>
                  String(row.status).toLowerCase() === 'pending' ? (
                    <>
                      <Button
                        size="sm"
                        variant="accent"
                        icon={Check}
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ id: row.id, decision: 'approve' })}
                        aria-label="Approve"
                      />
                      <Button
                        size="sm"
                        variant="danger"
                        icon={X}
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ id: row.id, decision: 'reject' })}
                        aria-label="Reject"
                      />
                    </>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => setActive(row)}>
                      View
                    </Button>
                  )
              : undefined
          }
          toolbar={
            <Toolbar>
              <SearchInput value={search} onChange={setSearch} placeholder="Search employee or reason…" width={240} />
              <FilterChip label="Type" value={typeFilter} options={leaveTypes} onChange={setTypeFilter} />
              {tab === 'all' ? (
                <FilterChip
                  label="Status"
                  value={statusFilter}
                  options={['Pending', 'Approved', 'Rejected']}
                  onChange={setStatusFilter}
                />
              ) : null}
              <ToolbarSpacer />
              <span style={{ fontSize: 11.5, color: 'var(--ac-faint)' }}>{fmtNumber(filtered.length)} requests</span>
            </Toolbar>
          }
        />
      </div>

      <LeaveDrawer
        request={active}
        allRequests={all}
        onClose={() => setActive(null)}
        canDecide={canDecide}
        onDecide={(decision) => decide.mutate({ id: active.id, decision })}
        deciding={decide.isPending}
      />

      <ConfirmDialog
        open={Boolean(bulkConfirm)}
        onClose={() => setBulkConfirm(null)}
        onConfirm={() => bulkDecide.mutate(bulkConfirm)}
        loading={bulkDecide.isPending}
        tone={bulkConfirm?.decision === 'approve' ? 'primary' : 'danger'}
        title={bulkConfirm?.decision === 'approve' ? 'Approve these requests?' : 'Reject these requests?'}
        confirmLabel={bulkConfirm?.decision === 'approve' ? 'Approve all' : 'Reject all'}
        message={
          <>
            You are about to {bulkConfirm?.decision} <strong>{bulkConfirm?.ids?.length}</strong> leave request
            {bulkConfirm?.ids?.length === 1 ? '' : 's'}. Each employee is notified of the decision.
          </>
        }
      />
    </div>
  );
}

/* =====================================================================
   REQUEST DRAWER
   ================================================================== */

function LeaveDrawer({ request, allRequests, onClose, canDecide, onDecide, deciding }) {
  const isPending = String(request?.status || '').toLowerCase() === 'pending';

  // This person's own history, so a decision can be made in context.
  const history = useMemo(() => {
    if (!request) return [];
    return allRequests.filter(
      (r) => r.employee_id === request.employee_id && r.id !== request.id
    );
  }, [request, allRequests]);

  const takenThisYear = useMemo(() => {
    const year = new Date().getFullYear();
    return history
      .filter(
        (r) =>
          String(r.status).toLowerCase() === 'approved' &&
          parseDate(r.start_date)?.getFullYear() === year
      )
      .reduce((sum, r) => sum + (Number(r.days) || daysBetween(r.start_date, r.end_date)), 0);
  }, [history]);

  // Anyone else already approved to be away on the same dates.
  const overlapping = useMemo(() => {
    if (!request?.start_date) return [];
    const start = request.start_date;
    const end = request.end_date || request.start_date;
    return allRequests.filter((r) => {
      if (r.id === request.id) return false;
      if (String(r.status).toLowerCase() !== 'approved') return false;
      const rs = r.start_date;
      const re = r.end_date || r.start_date;
      return rs && re && rs <= end && re >= start;
    });
  }, [request, allRequests]);

  const days = request ? request.days || daysBetween(request.start_date, request.end_date) : 0;

  return (
    <Drawer
      open={Boolean(request)}
      onClose={onClose}
      title={request?.employee_name || 'Leave request'}
      sub={request ? `${request.leave_type || 'Leave'} · ${days} day${days === 1 ? '' : 's'}` : null}
      badge={request ? <StatusChip value={request.status} /> : null}
      footer={
        canDecide && isPending ? (
          <>
            <Button variant="danger" icon={X} onClick={() => onDecide('reject')} loading={deciding}>
              Reject
            </Button>
            <div style={{ flex: '1 1 auto' }} />
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="accent" icon={Check} onClick={() => onDecide('approve')} loading={deciding}>
              Approve
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )
      }
    >
      {!request ? null : (
        <>
          <div className="ac-eyebrow" style={{ marginBottom: 7 }}>
            Request
          </div>
          <KeyValue
            items={[
              { key: 'Type', value: request.leave_type ? <Chip tone="info">{request.leave_type}</Chip> : null },
              { key: 'From', value: fmtDate(request.start_date) },
              { key: 'To', value: fmtDate(request.end_date || request.start_date) },
              { key: 'Duration', value: `${days} day${days === 1 ? '' : 's'}${request.duration ? ` · ${request.duration}` : ''}` },
              { key: 'Reason', value: request.reason },
              { key: 'Requested', value: `${fmtDate(request.created_at)} · ${timeAgo(request.created_at)}` },
              { key: 'Decided by', value: request.approved_by },
            ]}
          />

          <div className="ac-eyebrow" style={{ margin: '20px 0 7px' }}>
            This year
          </div>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span style={{ color: 'var(--ac-muted)' }}>Approved leave already taken</span>
            <strong className="ac-num">{takenThisYear} days</strong>
          </div>
          <Meter value={takenThisYear} max={Math.max(30, takenThisYear)} />
          <div style={{ fontSize: 11, color: 'var(--ac-faint)', marginTop: 5 }}>
            Across {history.filter((r) => String(r.status).toLowerCase() === 'approved').length} approved request(s).
          </div>

          <div className="ac-eyebrow" style={{ margin: '20px 0 7px' }}>
            Also away on these dates
          </div>
          {overlapping.length === 0 ? (
            <Notice tone="positive">Nobody else is on approved leave during this period.</Notice>
          ) : (
            <Notice tone="warning" title={`${overlapping.length} other person(s) already approved`}>
              <div style={{ marginTop: 5, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {overlapping.slice(0, 6).map((r) => (
                  <div key={r.id} style={{ fontSize: 12 }}>
                    {r.employee_name} · {fmtDate(r.start_date)} → {fmtDate(r.end_date || r.start_date)}
                  </div>
                ))}
              </div>
            </Notice>
          )}
        </>
      )}
    </Drawer>
  );
}
